import { add, differenceInSeconds, formatISO } from 'date-fns';
import { isEmpty, isNull } from 'lodash';
import { bool, node } from 'prop-types';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useFetch, useSafeState, useUser } from 'hooks';
import { routes } from 'router';
import { CART_TIMER_DURATION_MINUTES } from 'services';
import { setCart, setCartExpirationDate, setPurchaseModalOpen } from 'store';

const CartContext = createContext();

export const CartContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { currentUser } = useUser();
  const { showCart, cart } = useSelector((state) => state.cart);
  const { expirationDate } = useSelector((state) => state.persistedCart);
  const prevCart = useRef(null);

  const [timeRemaining, setTimeRemaining] = useSafeState(null);

  const { call: cartCall, loading: cartLoading } = useFetch({ withSetState: false });
  const { call: deleteOrderCall, loading: deleteLoading } = useFetch({ withSetState: false });
  const { call: deleteTotalCartCall, loading: deleteTotalCartLoading } = useFetch({ withSetState: false });

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getCart();
  }, [showCart, currentUser]);

  useEffect(() => {
    /**
     * Create cart expiration date if cart is not empty and expiration date is not set yet
     * The expiration date isn't based on the cart.createdAt in order to avoid issues with
     * client and server time differences
     */
    if (!isEmpty(cart?.lines) && !expirationDate) {
      let targetDate = add(new Date(), { minutes: CART_TIMER_DURATION_MINUTES });
      // Convert to ISO string because Date can't be stored in Redux
      targetDate = formatISO(targetDate);
      dispatch(setCartExpirationDate(targetDate));
    }
  }, [cart, expirationDate]);

  useEffect(() => {
    // Reset cart expiration date if cart is empty
    if (
      prevCart.current?.lines?.length > 0 &&
      isEmpty(cart?.lines) &&
      !location.pathname.includes(routes.leisurePurchase.path)
    ) {
      dispatch(setCartExpirationDate(null));
    }
    prevCart.current = cart;
  }, [cart, location.pathname]);

  useEffect(() => {
    initTimer();
  }, [cart, expirationDate]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (timeRemaining > 0) setTimeRemaining((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  useEffect(() => {
    // Automatically delete the cart if the countdown reaches 0
    if (!isNull(timeRemaining) && timeRemaining <= 0) deleteTotalCart(false);
  }, [timeRemaining]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const initTimer = () => {
    if (!cart) {
      setTimeRemaining(null);
    } else {
      // Set timer based on the creation date, by default it will be 20 minutes
      const expirationDate = add(new Date(cart.createdAt), { minutes: CART_TIMER_DURATION_MINUTES });
      let timeRemaining = differenceInSeconds(expirationDate, new Date());

      // Update timer based on the current date to avoid wrong countdowns
      if (!isEmpty(cart?.ttl)) {
        timeRemaining = differenceInSeconds(expirationDate, new Date(cart.ttl));
      }
      setTimeRemaining(timeRemaining);
    }
  };

  const getCart = async () => {
    if (!currentUser?.serialNumber) return;

    const { data } =
      (await cartCall({
        service: 'checkout',
        path: `/${currentUser.serialNumber}/cart/ticketing/`,
        method: 'get'
      })) || {};

    let cartData = data?.body;

    /**
     * Reset timer if cart is empty
     * Check if cartData is not undefined to avoid reset timer on the first render
     */
    if (cartData !== undefined && isEmpty(cartData?.lines)) {
      resetCart();
    }

    dispatch(setCart(cartData));
  };

  const deleteOrder = (order) => {
    return deleteOrderCall({
      method: 'delete',
      service: 'checkout',
      path: `/${currentUser.serialNumber}/cart/ticketing/product/${order.id}`,
      query: {
        articleId: order.productId,
        typeItem: order.typeItem
      }
    });
  };

  const deleteOneOrder = async (order) => {
    if (deleteLoading) return;

    const { data } = (await deleteOrder(order)) || {};

    if (data?.status === 200 || data?.status === 204) {
      const updatedCart = cart.lines.filter((line) => line.id !== order.id);
      if (isEmpty(updatedCart)) resetCart();
      getCart();
    }
  };

  const deleteTotalCart = async (manualDelete) => {
    if (isEmpty(cart?.lines) || deleteLoading || deleteTotalCartLoading || !currentUser?.serialNumber) return;
    let success = false;

    /**
     * Delete the cart for the Checkout API
     */
    const { data } =
      (await deleteTotalCartCall({
        method: 'delete',
        service: 'checkout',
        path: `${currentUser.serialNumber}/cart/ticketing/`
      })) || {};

    success = data?.status === 204;

    const leisurePurchaseRoute = location.pathname.includes(routes.leisurePurchase.path);

    if (success) {
      resetCart();

      if (leisurePurchaseRoute) {
        // Display warning message inside modal if cart was deleted while user was on leisure purchase page
        !showCart && dispatch(setPurchaseModalOpen(true));
      } else {
        // Display warning toast if cart was deleted while user was on any other page
        !manualDelete && toast.warning(t('cart:cartReset'), { autoClose: 5000 });
      }
    } else {
      toast.error(t('dialog:requestErrors.default'));
    }
  };

  const resetCart = () => {
    setTimeRemaining(null);
    dispatch(setCart(null));
    dispatch(setCartExpirationDate(null));
  };

  return (
    <CartContext.Provider
      value={{
        getCart,
        cartLoading,
        cart,
        deleteOneOrder,
        deleteTotalCart,
        deleteLoading,
        deleteTotalCartLoading,
        timeRemaining
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);

CartContextProvider.propTypes = {
  children: node.isRequired,
  showCart: bool
};
